import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { ButtonContent } from '../ui/ButtonContent';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  getInterviewSession,
  getInterviewResponses,
  deleteInterviewSession,
  closeSession,
  reopenSession,
  updateSessionSettings,
} from '../../services/interviewService';
import { generateCaseStudy } from '../../services/geminiService';
import type { Citation } from '../../services/geminiService';
import { useSupabase } from '../../lib/SupabaseProvider';
import { useOrg } from '../../lib/OrgProvider';
import { useReleaseFeature } from '@/lib/release-flags';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { requireAccessToken } from '../../lib/auth/requireAccessToken';
import { buildCloneNavigationState } from '../../lib/clone-session';
import { isFullResponse } from '../../lib/response-completion';
import { getResponseDisplayName } from '../../lib/admin-utils';
import type { InterviewSession, InterviewData } from '../../types';
import type { SettingsForm } from './CampaignSettings';
import Empty from '../common/Empty';
import { ConfirmDialog } from '../ui';
import { CampaignHeader } from './CampaignHeader';
import { CampaignResponseNavigation } from './CampaignResponseNavigation';
import { CampaignSettings } from './CampaignSettings';
import { useAdminLayout } from './AdminLayout';
import { CampaignNavigation, type CampaignTab } from './CampaignNavigation';
import { QuestionEditorSurface } from '../form-builder/QuestionEditorSurface';
import { StationaryPresence } from '../ui/StationaryPresence';
import { CaseStudyGenerator, RecipeGalleryView } from './CaseStudyGenerator';
import { CampaignReportParts, type CampaignReportPartsValue } from './CampaignReport';
import { RecipeResults } from './RecipeResults';
import { CampaignAskBox } from './CampaignAskBox';
import { RespondentList } from './RespondentList';
import { ResponseDetailView } from './ResponseDetailView';
import { ParticipantTable } from './automation/ParticipantTable';
import { useCampaignAutomationParticipants } from './automation/useCampaignAutomationParticipants';
import { AutomationSurfaceView } from './automation/AutomationSurfaceView';
import {
  cancelAutomationEnableOperation,
  enableAutomation,
  getAutomationEnableOperation,
  type AutomationEnableResult,
} from '../../services/automationService';
import {
  AUTOMATION_OPERATION_POLL_MS,
  AUTOMATION_OPERATION_TIMEOUT_MS,
  clearPendingAutomationEnable,
  readPendingAutomationEnable,
  writePendingAutomationEnable,
  type PendingAutomationEnableOperation,
} from '../../lib/api-contracts/automation/operation-lifecycle';
import { IconRefresh, IconX } from '@tabler/icons-react';
import { CampaignOverview, type ResponseSelection } from './analytics/CampaignOverview';

const CampaignQuestions = React.lazy(async () => {
  const module = await import('../AdminEditPage');
  return { default: module.AdminEditPage };
});

const AutomationSurface = React.lazy(async () => {
  const module = await import('./automation/AutomationSurface');
  return { default: module.AutomationSurface };
});

/** Read the shared terminal state from an enable result. */
function enableOperationStatus(
  result: AutomationEnableResult,
): 'completed' | 'unconfirmed' | 'cancelled' | null {
  // A flow row is itself proof that the transaction completed. Older completed
  // responses predate the operation metadata, so keep accepting those rows
  // while all non-terminal acknowledgements remain explicitly discriminated.
  return result.operation?.status ?? 'completed';
}

/** Poll one enable and request durable cancellation after 30 seconds. */
async function reconcileEnableOperation(
  campaignId: string,
  operation: PendingAutomationEnableOperation,
  accessToken: string,
  isActive: () => boolean,
  initialResult?: AutomationEnableResult,
): Promise<AutomationEnableResult> {
  let result = initialResult ?? await getAutomationEnableOperation(
    campaignId,
    operation.operationId,
    accessToken,
  );
  while (enableOperationStatus(result) === 'unconfirmed' && isActive()) {
    if (Date.now() - operation.startedAt >= AUTOMATION_OPERATION_TIMEOUT_MS) {
      result = await cancelAutomationEnableOperation(
        campaignId,
        operation.operationId,
        accessToken,
      );
    } else {
      await new Promise((resolve) => window.setTimeout(resolve, AUTOMATION_OPERATION_POLL_MS));
      if (!isActive()) break;
      result = await getAutomationEnableOperation(campaignId, operation.operationId, accessToken);
    }
    if (enableOperationStatus(result) === 'unconfirmed') {
      await new Promise((resolve) => window.setTimeout(resolve, AUTOMATION_OPERATION_POLL_MS));
    }
  }
  return result;
}

/**
 * Campaign detail page with master-detail pattern.
 * URL params: :id (session ID), :responseId (optional, auto-selects a response).
 *
 * Keeps analytics, responses, questions and automation in persistent navigation.
 * Individual responses open within the same campaign header.
 */
export const InterviewDetailView: React.FC = () => {
  const { id, responseId: urlResponseId } = useParams<{ id: string; responseId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { session: authSession, supabase } = useSupabase();
  const { hasPermission } = useOrg();
  const campaignAutomationEnabled = useReleaseFeature('campaignAutomation');
  const canEdit = hasPermission('interviews:edit');
  const canGenerateReports = hasPermission('interviews:generate_reports');
  const { refreshSidebar } = useAdminLayout();

  // Auto-open InviteModal when navigated from setup with showInvite state
  const showInviteOnMount = !!(location.state as { showInvite?: boolean })?.showInvite;

  // Auto-open Settings when navigated with ?tab=settings
  const showSettingsOnMount = new URLSearchParams(location.search).get('tab') === 'settings';
  // Auto-open the automation surface when navigated with ?tab=automation.
  const automationRequestedOnMount = new URLSearchParams(location.search).get('tab') === 'automation';
  const showAutomationOnMount = campaignAutomationEnabled && automationRequestedOnMount;

  // Core data
  const [session, setSession] = useState<InterviewSession | null>(null);
  const [responses, setResponses] = useState<InterviewData[]>([]);
  const [selectedResponseId, setSelectedResponseId] = useState<string | undefined>(urlResponseId);
  // Deep-link target from a campaign-ask citation: which response + canonical turn
  // to open. nonce bumps per click so jumping to the same turn twice re-fires.
  const [citationFocus, setCitationFocus] = useState<
    { responseId: string; turnIndex: number; nonce: number } | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Respondents behind a clicked Overview mark; narrows the response list until cleared.
  const [responseSelection, setResponseSelection] = useState<ResponseSelection | null>(null);
  // A selection belongs to one campaign's answers.
  useEffect(() => setResponseSelection(null), [id]);
  // A question the Overview hands to Ask hollie; each request gets a new nonce
  // and belongs to one campaign, so it never reaches another campaign's ask.
  const [askRequest, setAskRequest] = useState<{ campaignId: string; question: string; nonce: number } | null>(null);
  const askFromOverview = useCallback((question: string) => {
    if (!id) return;
    setAskRequest(previous => ({ campaignId: id, question, nonce: (previous?.nonce ?? 0) + 1 }));
  }, [id]);

  // Modal / panel state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmResponse, setDeleteConfirmResponse] = useState<InterviewData | null>(null);
  const [deletingResponse, setDeletingResponse] = useState(false);
  const [deleteResponseError, setDeleteResponseError] = useState<string | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const routeTab = new URLSearchParams(location.search).get('tab');
  const questionsRequested = canEdit && location.pathname.endsWith('/edit');
  const [editorCampaignId, setEditorCampaignId] = useState<string | null>(null);
  const [actionsTarget, setActionsTarget] = useState<HTMLDivElement | null>(null);
  const [feedbackTarget, setFeedbackTarget] = useState<HTMLDivElement | null>(null);
  const [activeTab, setActiveTab] = useState<CampaignTab>(questionsRequested ? 'questions' : routeTab === 'responses' ? 'responses' : routeTab === 'automate' || routeTab === 'automation' ? 'automate' : 'analytics');
  useEffect(() => {
    setActiveTab(questionsRequested ? 'questions' : routeTab === 'responses' ? 'responses' : routeTab === 'automate' || routeTab === 'automation' ? 'automate' : 'analytics');
  }, [routeTab, id, questionsRequested]);
  useEffect(() => {
    if (questionsRequested && id) setEditorCampaignId(id);
  }, [questionsRequested, id]);
  /** Keep campaign navigation and its URL synchronized without requiring a response. */
  const selectTab = (tab: CampaignTab) => {
    const scroller = campaignRootRef.current?.closest('main');
    if (scroller) {
      scrollPositions.current.set(scrollDestination, scroller.scrollTop);
      capturedScrollDestination.current = scrollDestination;
    }
    setActiveTab(tab);
    setSelectedResponseId(undefined);
    setDownloadReportFn(null);
    setShowAutomation(false);
    automationEntryOwnedRef.current = false;
    void navigate(`/admin/interviews/${id}${tab === 'questions' ? '/edit' : tab === 'analytics' ? '' : `?tab=${tab}`}`);
  };
  const [showAutomation, setShowAutomation] = useState(false);
  const [enablingAutomation, setEnablingAutomation] = useState(false);
  const [automationEntryError, setAutomationEntryError] = useState<string | null>(null);
  // Ref-based in-flight lock: two synchronous clicks both read `enablingAutomation`
  // state as false, so the ref (updated synchronously) is what prevents a
  // double enable POST.
  const enablingAutomationRef = useRef(false);
  // Keeps the automation route owned while a retained first-enable receipt is
  // reconciling, even before the flow-status hook can observe its new row.
  const automationEntryOwnedRef = useRef(false);
  // A monotonic campaign scope fences every load and automation-entry
  // completion. React Router reuses this component when only `:id` changes, so
  // an older request must never commit into the newly selected campaign.
  const campaignGenerationRef = useRef(0);
  const routeIdRef = useRef(id);
  routeIdRef.current = id;
  const campaignRootRef = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef(new Map<string, number>());
  const capturedScrollDestination = useRef<string | null>(null);
  const scrollDestination = `${id}:${urlResponseId ? `response:${urlResponseId}` : activeTab}`;
  useLayoutEffect(() => {
    const scroller = campaignRootRef.current?.closest('main');
    if (!scroller || loading) return undefined;
    const desired = scrollPositions.current.get(scrollDestination) ?? 0;
    scroller.scrollTop = desired;
    return () => {
      if (capturedScrollDestination.current !== scrollDestination) scrollPositions.current.set(scrollDestination, scroller.scrollTop);
      capturedScrollDestination.current = null;
    };
  }, [scrollDestination, loading]);
  const handleEditorSessionChange = useCallback((updated: InterviewSession) => {
    if (routeIdRef.current === updated.id) setSession(updated);
  }, []);
  const isCurrentCampaign = (campaignId: string, generation: number): boolean => (
    campaignGenerationRef.current === generation && routeIdRef.current === campaignId
  );

  // Participant-view data (flow existence, participants, add/remove/pause). The
  // block-sequence editing lives only in AutomationSurface; this powers the
  // header entry point and the overview people table.
  const automation = useCampaignAutomationParticipants(
    campaignAutomationEnabled ? id ?? '' : '',
  );

  // Response report download (passed up from ResponseDetailView)
  const [downloadReportFn, setDownloadReportFn] = useState<(() => void) | null>(null);
  const handleReportReady = useCallback((fn: (() => void) | null) => {
    setDownloadReportFn(() => fn);
  }, []);

  // Campaign report state
  const [generatingCampaignReport, setGeneratingCampaignReport] = useState(false);
  const [campaignReportError, setCampaignReportError] = useState<string | null>(null);
  const [validReportCount, setValidReportCount] = useState(0);


  /**
   * Once-guard for the auto-regen effect.
   * Tracks the last session ID for which we already attempted auto-regeneration
   * so that re-renders (state updates, parent re-renders) cannot re-fire a
   * PAID generation that already started or was deliberately skipped.
   * Reset to null whenever `id` changes so a fresh campaign load gets one attempt.
   */
  const autoRegenFiredForRef = useRef<string | null>(null);

  useEffect(() => {
    if (id) {
      const generation = campaignGenerationRef.current + 1;
      campaignGenerationRef.current = generation;
      const pendingEnable = campaignAutomationEnabled
        ? readPendingAutomationEnable(id)
        : null;
      automationEntryOwnedRef.current = Boolean(pendingEnable);
      // Reset the once-guard whenever we navigate to a different campaign.
      autoRegenFiredForRef.current = null;
      enablingAutomationRef.current = Boolean(pendingEnable);
      setEnablingAutomation(Boolean(pendingEnable));
      setAutomationEntryError(null);
      setShowAutomation(Boolean(pendingEnable));
      setShowDeleteModal(false);
      setDeleteConfirmResponse(null);
      setShowCloseModal(false);
      setShowReopenModal(false);
      setShowSettings(false);
      setDeleting(false);
      setDeletingResponse(false);
      setClosing(false);
      setReopening(false);
      setSavingSettings(false);
      setGeneratingCampaignReport(false);
      setDeleteError(null);
      setDeleteResponseError(null);
      setCloseError(null);
      setReopenError(null);
      setCampaignReportError(null);
      setCitationFocus(null);
      setDownloadReportFn(null);
      void loadData(id, generation);
      if (pendingEnable) {
        void (async () => {
          try {
            const accessToken = await requireAccessToken(supabase);
            const result = await reconcileEnableOperation(
              id,
              pendingEnable,
              accessToken,
              () => isCurrentCampaign(id, generation),
            );
            if (!isCurrentCampaign(id, generation)) return;
            const status = enableOperationStatus(result);
            if (status === 'completed') {
              clearPendingAutomationEnable(id);
              automation.reload();
              setShowAutomation(true);
              setAutomationEntryError(null);
            } else if (status === 'cancelled') {
              clearPendingAutomationEnable(id);
              automationEntryOwnedRef.current = false;
              setShowAutomation(false);
              setAutomationEntryError("Couldn't create automation.");
            } else {
              return;
            }
            enablingAutomationRef.current = false;
            setEnablingAutomation(false);
          } catch (resumeError) {
            captureException(resumeError as Error, 'InterviewDetailView.resumeAutomation', {
              sessionId: id,
              operationId: pendingEnable.operationId,
            });
          }
        })();
      }
      return () => {
        if (campaignGenerationRef.current === generation) {
          campaignGenerationRef.current += 1;
        }
      };
    }
    return undefined;
  }, [id, campaignAutomationEnabled]);

  // Sync URL responseId to state (clears when navigating back to overview)
  useEffect(() => {
    setSelectedResponseId(urlResponseId);
  }, [urlResponseId]);

  // Auto-open settings panel when navigated with ?tab=settings
  useEffect(() => {
    if (showSettingsOnMount && session && !loading) {
      setShowSettings(true);
    }
  }, [showSettingsOnMount, session, loading]);

  // A disabled release flag owns the route as well as the visible entry points.
  // Replace stale or manually-entered automation deep links with the overview.
  useEffect(() => {
    if (campaignAutomationEnabled) return;
    automationEntryOwnedRef.current = false;
    setShowAutomation(false);
    setAutomationEntryError(null);
    if (automationRequestedOnMount && id) {
      void navigate(`/admin/interviews/${id}`, { replace: true });
    }
  }, [automationRequestedOnMount, campaignAutomationEnabled, id, navigate]);

  // Auto-open the automation surface when navigated with ?tab=automation
  useEffect(() => {
    if (session && !loading) {
      setShowAutomation(
        campaignAutomationEnabled && canEdit && (
          automationEntryOwnedRef.current
          || (showAutomationOnMount && (automation.hasFlow || enablingAutomationRef.current))
        ),
      );
    }
  }, [
    showAutomationOnMount,
    session,
    loading,
    canEdit,
    campaignAutomationEnabled,
    automation.hasFlow,
  ]);

  /** Load session and response data from the API. */
  const loadData = async (
    campaignId: string | undefined = id,
    generation: number = campaignGenerationRef.current,
  ) => {
    if (!campaignId) return;
    if (!isCurrentCampaign(campaignId, generation)) return;

    // Retain the previous state while the new request runs, but the render gate
    // below suppresses it until its campaign id matches the current route.
    if (!session || session.id !== campaignId) setLoading(true);
    setError(null);
    // Clear any stale generation error from the previous campaign so it does
    // not persist across switches.
    setCampaignReportError(null);

    try {
      // Fetch session, responses, per-response report count, AND the current
      // campaign_report row together so the auto-regen effect sees a fully
      // consistent snapshot on the first render after load.
      // Fetching all four in parallel avoids any race where session is from the
      // new campaign but validReportCount or generatingCampaignReport is still
      // from the previous one.
      const [sessionData, responsesData, reportCountResult, campaignReportResult] = await Promise.all([
        getInterviewSession(campaignId),
        getInterviewResponses(campaignId),
        supabase
          .from('case_studies_visible')
          .select('id', { count: 'exact', head: true })
          .eq('interview_session_id', campaignId)
          .eq('content_type', 'report')
          .not('interview_data_id', 'is', null)
          .not('content', 'like', '__GEN_IN_PROGRESS__:%'),
        supabase
          .from('case_studies_visible')
          .select('content')
          .eq('interview_session_id', campaignId)
          .eq('content_type', 'campaign_report')
          .is('interview_data_id', null)
          .maybeSingle(),
      ]);

      if (!isCurrentCampaign(campaignId, generation)) return;

      if (!sessionData) {
        setSession(null);
        setError('Session not found');
        return;
      }

      // Detect a server-side generation lock so we can show the generating
      // state immediately on page load instead of an error or empty fallback.
      const isInProgress = typeof campaignReportResult.data?.content === 'string'
        && campaignReportResult.data.content.startsWith('__GEN_IN_PROGRESS__:');

      // All setState calls land in the same synchronous block so React commits
      // one atomic render with consistent session + responses + validReportCount
      // + generatingCampaignReport.
      setSession(sessionData);
      setResponses(responsesData);
      setValidReportCount(reportCountResult.count ?? 0);
      if (isInProgress) {
        setGeneratingCampaignReport(true);
      }

    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'InterviewDetailView.loadData', { interviewId: campaignId });
      if (!isCurrentCampaign(campaignId, generation)) return;
      setSession(null);
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      if (isCurrentCampaign(campaignId, generation)) setLoading(false);
    }
  };

  /** Navigate to the selected response and update state. */
  const handleSelectResponse = (responseId: string) => {
    setSelectedResponseId(responseId);
    void navigate(`/admin/interviews/${id}/responses/${responseId}`, { replace: true });
  };

  /**
   * Open the response a campaign-ask citation points at and focus the cited turn.
   * No-op if the citation's conversation is not in the loaded set.
   */
  const handleAskCitation = (citation: Citation) => {
    if (!responses.some((r) => r.id === citation.conversation_id)) return;
    handleSelectResponse(citation.conversation_id);
    setCitationFocus((prev) => ({
      responseId: citation.conversation_id,
      turnIndex: citation.turn_index,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
  };

  // --- Delete handlers ---
  const handleDeleteClick = () => { setDeleteError(null); setShowDeleteModal(true); };
  const handleDeleteCancel = () => { setShowDeleteModal(false); setDeleteError(null); };

  // --- Response Delete handlers ---
  const handleDeleteResponseClick = (response: InterviewData | string) => {
    const res = typeof response === 'string' ? responses.find(r => r.id === response) : response;
    if (res) {
      setDeleteResponseError(null);
      setDeleteConfirmResponse(res);
    }
  };

  const handleDeleteResponseCancel = () => {
    setDeleteConfirmResponse(null);
    setDeleteResponseError(null);
  };

  /** Permanently delete an individual response */
  const handleDeleteResponseConfirm = async () => {
    if (!session || !deleteConfirmResponse) return;
    const campaignId = session.id;
    const responseId = deleteConfirmResponse.id;
    const generation = campaignGenerationRef.current;

    setDeletingResponse(true);
    try {
      const { deleteInterviewResponse } = await import('../../services/interviewService');
      await deleteInterviewResponse(responseId, campaignId);
      refreshSidebar();
      if (!isCurrentCampaign(campaignId, generation)) return;

      setResponses(prev => prev.filter(r => r.id !== responseId));

      if (selectedResponseId === responseId) {
        setSelectedResponseId(undefined);
        void navigate(`/admin/interviews/${campaignId}`, { replace: true });
      }

      setDeleteConfirmResponse(null);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'InterviewDetailView.deleteResponse', { sessionId: campaignId, responseId });
      if (isCurrentCampaign(campaignId, generation)) {
        setDeleteResponseError("Couldn't delete response.");
      }
    } finally {
      if (isCurrentCampaign(campaignId, generation)) setDeletingResponse(false);
    }
  };

  /** Permanently delete the interview session. */
  const handleDeleteConfirm = async () => {
    if (!session) return;
    const campaignId = session.id;
    const generation = campaignGenerationRef.current;

    setDeleting(true);
    try {
      await deleteInterviewSession(campaignId);
      refreshSidebar();
      if (!isCurrentCampaign(campaignId, generation)) return;
      void navigate('/admin');
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'InterviewDetailView.deleteInterview', { sessionId: campaignId });
      if (isCurrentCampaign(campaignId, generation)) setDeleteError("Couldn't delete.");
    } finally {
      if (isCurrentCampaign(campaignId, generation)) setDeleting(false);
    }
  };

  // --- Close handlers ---
  const handleCloseClick = () => { setCloseError(null); setShowCloseModal(true); };
  const handleCloseCancel = () => { setShowCloseModal(false); setCloseError(null); };

  /** Close the campaign to prevent new responses. */
  const handleCloseConfirm = async () => {
    if (!session) return;
    const campaignId = session.id;
    const generation = campaignGenerationRef.current;

    setClosing(true);
    try {
      await closeSession(campaignId);
      if (!isCurrentCampaign(campaignId, generation)) return;
      setSession(prev => prev ? { ...prev, status: 'completed' as const } : null);
      setShowCloseModal(false);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'InterviewDetailView.closeSession', { sessionId: campaignId });
      if (isCurrentCampaign(campaignId, generation)) setCloseError("Couldn't close campaign.");
    } finally {
      if (isCurrentCampaign(campaignId, generation)) setClosing(false);
    }
  };

  // --- Reopen handlers ---
  const handleReopenClick = () => { setReopenError(null); setShowReopenModal(true); };
  const handleReopenCancel = () => { setShowReopenModal(false); setReopenError(null); };

  /** Re-open a closed campaign to allow new responses. */
  const handleReopenConfirm = async () => {
    if (!session) return;
    const campaignId = session.id;
    const generation = campaignGenerationRef.current;

    setReopening(true);
    try {
      await reopenSession(campaignId);
      if (!isCurrentCampaign(campaignId, generation)) return;
      setSession(prev => prev ? {
        ...prev,
        status: 'active' as const,
        closes_at: null,
        ...(prev.opens_at && new Date(prev.opens_at) > new Date() ? { opens_at: null } : {}),
      } : null);
      setShowReopenModal(false);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'InterviewDetailView.reopenSession', { sessionId: campaignId });
      if (isCurrentCampaign(campaignId, generation)) setReopenError("Couldn't reopen campaign.");
    } finally {
      if (isCurrentCampaign(campaignId, generation)) setReopening(false);
    }
  };

  /**
   * Navigate to setup with pre-filled context AND the source form_schema so
   * the wizard lands on the builder with the existing questions ready to
   * edit. Without `initialFields`, the wizard falls back to the objectives
   * step and the user has to start the conversation from scratch.
   */
  const handleCloneClick = () => {
    if (!session) return;
    void navigate('/admin/setup', { state: buildCloneNavigationState(session) });
  };

  /** Open the edit-configuration page for this campaign. */
  const handleEditClick = () => {
    if (!session) return;
    void navigate(`/admin/interviews/${session.id}/edit`);
  };

  /**
   * One-click automation entry. When a flow already exists, just open the
   * surface. Otherwise open its loading shell immediately, then enable it with
   * the admin's browser timezone. A failed generation returns to the overview
   * so its inline retry remains available.
   */
  const handleAutomationClick = async () => {
    if (!campaignAutomationEnabled || !session || !canEdit) return;
    const campaignId = session.id;
    const generation = campaignGenerationRef.current;
    setActiveTab('automate');
    if (automation.hasFlow) {
      setAutomationEntryError(null);
      setShowAutomation(true);
      void navigate(`/admin/interviews/${session.id}?tab=automation`);
      return;
    }
    // Do not route to enable until the flow status is known: a click during the
    // load could otherwise re-enable a deliberately-disabled flow.
    if (automation.loading) return;
    // Ref lock beats the render-closure state read on a synchronous double-click.
    if (enablingAutomationRef.current) return;
    enablingAutomationRef.current = true;
    automationEntryOwnedRef.current = true;
    setAutomationEntryError(null);
    setEnablingAutomation(true);
    setShowAutomation(true);
    void navigate(`/admin/interviews/${campaignId}?tab=automation`);
    let terminal = false;
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const accessToken = await requireAccessToken(supabase);
      if (!isCurrentCampaign(campaignId, generation)) return;
      const operation: PendingAutomationEnableOperation = {
        operationId: crypto.randomUUID(),
        desiredEnabled: true,
        startedAt: Date.now(),
      };
      writePendingAutomationEnable(campaignId, operation);
      let initialResult: AutomationEnableResult;
      try {
        initialResult = await enableAutomation(
          campaignId,
          timezone,
          accessToken,
          operation.operationId,
        );
      } catch (requestError) {
        captureException(requestError as Error, 'InterviewDetailView.enableAutomationAmbiguous', {
          sessionId: campaignId,
          operationId: operation.operationId,
        });
        initialResult = await getAutomationEnableOperation(
          campaignId,
          operation.operationId,
          accessToken,
        );
      }
      const result = await reconcileEnableOperation(
        campaignId,
        operation,
        accessToken,
        () => isCurrentCampaign(campaignId, generation),
        initialResult,
      );
      if (!isCurrentCampaign(campaignId, generation)) return;
      const status = enableOperationStatus(result);
      if (status === 'completed') {
        clearPendingAutomationEnable(campaignId);
        terminal = true;
      } else if (status === 'cancelled') {
        clearPendingAutomationEnable(campaignId);
        terminal = true;
        throw new Error('Enable operation was cancelled');
      } else {
        return;
      }
      automation.reload();
      setAutomationEntryError(null);
    } catch (err) {
      captureException(
        err instanceof Error ? err : String(err),
        'InterviewDetailView.enableAutomation',
        { sessionId: campaignId },
      );
      if (!isCurrentCampaign(campaignId, generation)) return;
      if (terminal || !readPendingAutomationEnable(campaignId)) {
        automationEntryOwnedRef.current = false;
        setShowAutomation(false);
        void navigate(`/admin/interviews/${campaignId}`, { replace: true });
        setAutomationEntryError("Couldn't create automation.");
      }
    } finally {
      if (
        isCurrentCampaign(campaignId, generation)
        && (terminal || !readPendingAutomationEnable(campaignId))
      ) {
        enablingAutomationRef.current = false;
        setEnablingAutomation(false);
      }
    }
  };

  /** Return from the URL-owned automation tab to the campaign overview. */
  const handleCloseAutomation = (): void => {
    if (!session) return;
    automationEntryOwnedRef.current = false;
    setShowAutomation(false);
    automation.reload();
    setActiveTab('automate');
    void navigate(`/admin/interviews/${session.id}?tab=automate`);
  };

  /** Save updated campaign settings. */
  const handleSaveSettings = async (settings: SettingsForm) => {
    if (!session) return;
    const campaignId = session.id;
    const generation = campaignGenerationRef.current;

    setSavingSettings(true);
    setError(null);

    // Validate opens_at < closes_at
    if (settings.opens_at && settings.closes_at) {
      if (new Date(settings.opens_at) >= new Date(settings.closes_at)) {
        setError('Open date must be before close date');
        setSavingSettings(false);
        return;
      }
    }

    try {
      const updatedSession = await updateSessionSettings(campaignId, settings);
      if (!isCurrentCampaign(campaignId, generation)) return;
      setSession(updatedSession);
      setShowSettings(false);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'InterviewDetailView.saveSettings', { sessionId: campaignId });
      if (isCurrentCampaign(campaignId, generation)) {
        setError(err instanceof Error ? err.message : 'Failed to save settings');
      }
    } finally {
      if (isCurrentCampaign(campaignId, generation)) setSavingSettings(false);
    }
  };

  // Computed values
  const completedCount = responses.filter(isFullResponse).length;
  const canGenerateCampaignReport = completedCount >= 2;
  const visibleResponses = useMemo(() => {
    if (!responseSelection) return responses;
    const ids = new Set(responseSelection.ids);
    return responses.filter(response => ids.has(response.id));
  }, [responses, responseSelection]);

  const newResponsesSinceReport = useMemo(() => {
    const storedCount = session?.aggregated_report_included_count ?? 0;
    return Math.max(0, validReportCount - storedCount);
  }, [session?.aggregated_report_included_count, validReportCount]);

  /**
   * Generates (or regenerates) the aggregated campaign report.
   * Uses the same generateCaseStudy service but with 'campaign_report' content type.
   */
  const handleGenerateCampaignReport = async () => {
    if (!canGenerateReports || !session || !authSession?.access_token) return;
    const campaignId = session.id;
    const generation = campaignGenerationRef.current;

    setGeneratingCampaignReport(true);
    setCampaignReportError(null);

    try {
      const result = await generateCaseStudy(
        campaignId,
        authSession.access_token,
        'campaign_report',
      );
      if (!isCurrentCampaign(campaignId, generation)) return;

      if (result.success) {
        // Reload all data including the new report count
        void loadData(campaignId, generation);
      } else {
        setCampaignReportError(result.error || 'Campaign report generation failed');
      }
    } catch (err) {
      captureException(
        err instanceof Error ? err : String(err),
        'InterviewDetailView.generateCampaignReport',
        { sessionId: campaignId },
      );
      if (isCurrentCampaign(campaignId, generation)) {
        setCampaignReportError(err instanceof Error ? err.message : 'Campaign report generation failed');
      }
    } finally {
      if (isCurrentCampaign(campaignId, generation)) setGeneratingCampaignReport(false);
    }
  };

  /**
   * Rebuild a stored report from an older pipeline (for example the pooled
   * findings before the synthesized brief) once per page load. Shares the
   * auto-regen once-guard, so a page load makes at most one automatic call.
   */
  const regenerateOutdatedReport = () => {
    if (!session || autoRegenFiredForRef.current === session.id || generatingCampaignReport) return;
    autoRegenFiredForRef.current = session.id;
    void handleGenerateCampaignReport();
  };

  // Auto-generate campaign report when new per-response reports exist.
  // Guards:
  // 1. Skip if we already fired for this session ID (once-guard prevents
  //    re-renders from triggering duplicate paid generations).
  // 2. Skip if a generation is already in progress (either component state
  //    or a DB-level lock detected during loadData).
  useEffect(() => {
    if (!canGenerateReports || loading || !session || session.id !== id || !authSession?.access_token) return;
    if (validReportCount < 2 || !canGenerateCampaignReport) return;
    if (generatingCampaignReport) return;
    // Once-guard: only attempt auto-regen once per campaign ID per page load.
    if (autoRegenFiredForRef.current === session.id) return;
    const storedCount = session.aggregated_report_included_count ?? 0;
    if (validReportCount > storedCount) {
      autoRegenFiredForRef.current = session.id;
      void handleGenerateCampaignReport();
    }
  }, [canGenerateReports, canGenerateCampaignReport, loading, session, validReportCount, authSession?.access_token]);

  const selectedResponse = selectedResponseId
    ? responses.find(r => r.id === selectedResponseId)
    : undefined;


  // Unknown data stays inside the actual destination layout. Never expose a
  // previous campaign's evidence or action callbacks during a route switch.
  if ((session && session.id !== id) || (!session && loading)) {
    const pendingTab: CampaignTab = urlResponseId ? 'responses'
      : questionsRequested && canEdit ? 'questions'
        : routeTab === 'responses' ? 'responses'
          : routeTab === 'automate' || routeTab === 'automation' ? 'automate' : 'analytics';
    return <div ref={campaignRootRef} role="status" aria-label="Loading campaign" aria-busy="true" className="pt-16 md:pt-0 [overflow-anchor:none]">
      <CampaignHeader session={null} responseCount={0} completedCount={0}
        navigation={<CampaignNavigation sessionId={id ?? ''} active={pendingTab} canEdit={canEdit} onSelect={selectTab} />}
        onClose={handleCloseClick} onClone={handleCloneClick} onDelete={handleDeleteClick} />
      {pendingTab === 'questions' ? <QuestionEditorSurface /> : <main className={urlResponseId ? '' : 'container mx-auto px-4 py-6'}>
        {urlResponseId ? <ResponseDetailView loading sessionId={id ?? ''}
          navigation={<CampaignResponseNavigation responses={[]} onSelect={handleSelectResponse} />} />
          : pendingTab === 'responses' ? <RespondentList loading responses={[]} onSelectResponse={handleSelectResponse} />
            : pendingTab === 'automate' ? campaignAutomationEnabled && canEdit && routeTab === 'automation'
              ? <AutomationSurfaceView loading /> : <RecipeGalleryView loading />
              : <CampaignOverview />}
      </main>}
    </div>;
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-danger mb-4">{error || 'Session not found'}</p>
          <button
            onClick={() => navigate('/admin')}
            className="text-volt-soft hover:text-volt-soft"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // The campaign ask launcher, a small button in the Overview's Ask hollie card.
  const askCard = (
    <CampaignAskBox key={session.id} interviewId={session.id} onCitationClick={handleAskCitation} request={askRequest?.campaignId === session.id ? askRequest : undefined} variant="button" />
  );

  /**
   * The one campaign report: the deterministic Overview with the written
   * summary beside Ask hollie and the written sections after the highlights.
   */
  const renderOverview = (parts?: CampaignReportPartsValue) => (
      <CampaignOverview
        key={session.id}
        session={session}
        refreshKey={`${responses.length}:${completedCount}`}
        onOpenResponses={selection => { setResponseSelection(selection); selectTab('responses'); }}
        onAsk={canGenerateCampaignReport ? askFromOverview : undefined}
        askBox={canGenerateCampaignReport ? askCard : undefined}
        summary={parts?.summary}
        recipeResults={<RecipeResults sessionId={session.id} />}
        report={parts?.body}
        updated={parts?.updated}
      />
  );

  const showQuestions = canEdit && activeTab === 'questions' && !selectedResponse;

  return (
    <div ref={campaignRootRef} className="pt-16 md:pt-0 [overflow-anchor:none]">
      <CampaignHeader
        session={session}
        responseCount={responses.length}
        completedCount={completedCount}
        navigation={<CampaignNavigation sessionId={session.id} active={selectedResponse ? 'responses' : activeTab} responseCount={responses.length} canEdit={canEdit} onSelect={selectTab} />}
        onAutomation={campaignAutomationEnabled && canEdit
          ? () => void handleAutomationClick()
          : undefined}
        hasAutomation={campaignAutomationEnabled && automation.hasFlow}
        automationDisabled={automation.loading || enablingAutomation}
        onClose={handleCloseClick}
        onReopen={handleReopenClick}
        onEdit={handleEditClick}
        onClone={handleCloneClick}
        onDelete={handleDeleteClick}
        onDeleteResponse={selectedResponse ? () => handleDeleteResponseClick(selectedResponse) : undefined}
        onDownloadReport={downloadReportFn || undefined}
        selectedResponse={selectedResponse}
        contextualToolbar={<div className="shrink-0" hidden={!showQuestions} aria-hidden={!showQuestions} inert={!showQuestions}>
          <div ref={setActionsTarget} role="toolbar" aria-label="Question actions" />
          <div ref={setFeedbackTarget} />
        </div>}
        initialInviteOpen={showInviteOnMount}
        onInvitePeople={campaignAutomationEnabled && automation.canInvite
          ? automation.addParticipants
          : undefined}
      />

      {campaignAutomationEnabled && automationEntryError && (
        <div
          role="alert"
          className="container mx-auto mt-4 flex items-center justify-between gap-4 rounded-lg bg-wash-danger px-4 py-3 text-sm font-medium text-danger-soft"
        >
          <span>{automationEntryError}</span>
          <button
            type="button"
            onClick={() => void handleAutomationClick()}
            disabled={enablingAutomation}
            aria-busy={enablingAutomation}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-background disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ButtonContent loading={enablingAutomation}>
              <IconRefresh aria-hidden="true" className="h-4 w-4" />
              Retry
            </ButtonContent>
          </button>
        </div>
      )}

      <div className="relative">
      <StationaryPresence show={showQuestions} keepMounted data-campaign-panel="questions">
        {(showQuestions || editorCampaignId === session.id) && <React.Suspense fallback={<QuestionEditorSurface />}>
          <CampaignQuestions key={session.id} loadedSession={session} responseCount={responses.length}
            actionsTarget={actionsTarget} feedbackTarget={feedbackTarget} onSessionChange={handleEditorSessionChange} />
        </React.Suspense>}
      </StationaryPresence>

      <StationaryPresence show={!showQuestions}>
      <main className={selectedResponse ? '' : 'container mx-auto px-4 py-6'}>
        {selectedResponse ? (
          <ResponseDetailView
            initialTab={routeTab === 'recipes' ? 'create' : undefined}
            sessionId={session.id}
            response={selectedResponse}
            session={session}
            navigation={<CampaignResponseNavigation response={selectedResponse} responses={responses} onSelect={handleSelectResponse} />}
            onReportReady={handleReportReady}
            focusTurnIndex={
              citationFocus?.responseId === selectedResponse.id ? citationFocus.turnIndex : undefined
            }
            focusTurnKey={
              citationFocus?.responseId === selectedResponse.id ? citationFocus.nonce : undefined
            }
          />
        ) : campaignAutomationEnabled && showAutomation && canEdit ? enablingAutomation ? (
          <AutomationSurfaceView loading loadingLabel="Creating automation" />
        ) : (
          <div className="space-y-6">
            <React.Suspense fallback={<AutomationSurfaceView loading />}>
              <AutomationSurface
                key={session.id}
                session={session}
                onClose={handleCloseAutomation}
                onEnabledChange={automation.reload}
                onInvitePeople={automation.canInvite ? automation.addParticipants : undefined}
              />
            </React.Suspense>
              {automation.error && (
                <div role="alert" className="rounded-2xl bg-danger px-6 py-4 text-danger-foreground">
                  <p className="text-sm font-medium">{automation.error}</p>
                  <button
                    type="button"
                    onClick={automation.reload}
                    className="mt-4 cursor-pointer rounded-lg bg-background px-6 py-3 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-surface-hover"
                  >
                    Retry
                  </button>
                </div>
              )}
            {automation.hasFlow && <ParticipantTable
              participants={automation.participants}
              responses={[]}
              onSelectResponse={handleSelectResponse}
              onDelete={automation.deleteParticipant}
              onTogglePause={automation.togglePause}
              flowBlocks={automation.blocks}
              layout="full"
              canManage={canEdit}
            />}
          </div>
        ) : (
          // The active destination owns layout; retiring data fades in place.
          <div className="relative">
            <StationaryPresence show={activeTab === 'automate'} data-campaign-panel="automate">
              <div className="space-y-6">
                <CaseStudyGenerator key={session.id} interviewId={session.id} interviewStatus={session.status} hasTranscript={false} campaignMode />
              </div>
            </StationaryPresence>
            <StationaryPresence show={activeTab === 'analytics'} data-campaign-panel="analytics" className="flex flex-col gap-6">
              {canGenerateCampaignReport ? (
                <CampaignReportParts
                  session={session}
                  completedCount={completedCount}
                  newResponsesSinceReport={newResponsesSinceReport}
                  generatingReport={generatingCampaignReport}
                  reportError={campaignReportError}
                  canGenerate={canGenerateReports}
                  onGenerate={handleGenerateCampaignReport}
                  onOutdated={regenerateOutdatedReport}
                >
                  {renderOverview}
                </CampaignReportParts>
              ) : completedCount >= 1 ? (
                // Exactly one response: the whole campaign is just that single
                // conversation, so asking "the campaign" adds nothing here (the
                // ask lives on the response itself). No ask card on the overview.
                <>
                  {renderOverview()}
                  <div className="bg-background/50 border border-border/50 rounded-2xl p-8">
                    <Empty
                      title="Summary coming soon"
                      message="Your campaign summary unlocks at 2 completed responses."
                    />
                  </div>
                </>
              ) : (
                <div className="bg-background/50 border border-border/50 rounded-2xl p-8">
                  <Empty
                    title={responses.length > 0 ? 'No completed responses yet' : 'No responses yet'}
                    message={
                      responses.length > 0
                        ? 'Responses appear here as participants finish their conversations.'
                        : 'Invite participants using the share link to start collecting responses.'
                    }
                  />
                </div>
              )}
            </StationaryPresence>

            <StationaryPresence show={activeTab === 'responses'} data-campaign-panel="responses" className="min-w-0 flex flex-col gap-6">
              {responseSelection && (
                <div className="flex items-center justify-between gap-3 rounded-2xl bg-wash-accent px-5 py-3 text-sm text-foreground">
                  <span className="min-w-0 truncate" title={responseSelection.label}>{responseSelection.label}</span>
                  <button
                    type="button"
                    aria-label={`Clear ${responseSelection.label}`}
                    onClick={() => setResponseSelection(null)}
                    className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    <IconX aria-hidden="true" className="h-4 w-4" />
                  </button>
                </div>
              )}
              <RespondentList
                responses={visibleResponses}
                onSelectResponse={handleSelectResponse}
                onDeleteResponse={handleDeleteResponseClick}
              />
            </StationaryPresence>
          </div>
        )}
      </main>
      </StationaryPresence>
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete?"
        description={
          <>
            Are you sure you want to delete <span className="font-semibold text-foreground">{session.target_name}</span>?
            This action cannot be undone and will permanently delete all data and <span className="font-semibold text-foreground">{responses.length} response{responses.length !== 1 ? 's' : ''}</span>.
          </>
        }
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
        error={deleteError}
      />

      {/* Delete Response confirmation */}
      <ConfirmDialog
        open={!!deleteConfirmResponse}
        onClose={handleDeleteResponseCancel}
        onConfirm={handleDeleteResponseConfirm}
        title="Delete Response?"
        description={
          <>
            Are you sure you want to delete the response from <span className="font-semibold text-foreground">{getResponseDisplayName(deleteConfirmResponse ?? {})}</span>?
            This cannot be undone.
          </>
        }
        confirmLabel="Yes, Delete Response"
        loading={deletingResponse}
        variant="danger"
        error={deleteResponseError}
      />

      {/* Close campaign confirmation */}
      <ConfirmDialog
        open={showCloseModal}
        onClose={handleCloseCancel}
        onConfirm={handleCloseConfirm}
        title="Close Campaign?"
        description={
          <>
            Are you sure you want to close <span className="font-semibold text-foreground">{session.target_name}</span>?
            This will prevent new responses from being submitted. Existing {responses.length} response{responses.length !== 1 ? 's' : ''} will not be affected.
          </>
        }
        confirmLabel="Yes, Close Campaign"
        loading={closing}
        variant="warning"
        error={closeError}
      />

      {/* Reopen campaign confirmation */}
      <ConfirmDialog
        open={showReopenModal}
        onClose={handleReopenCancel}
        onConfirm={handleReopenConfirm}
        title="Re-open Campaign?"
        description={
          <>
            Are you sure you want to re-open <span className="font-semibold text-foreground">{session.target_name}</span>?
            This will allow new responses to be submitted again. Any scheduled closing or expiry dates will be cleared.
          </>
        }
        confirmLabel="Yes, Re-open Campaign"
        loading={reopening}
        variant="warning"
        error={reopenError}
      />

      {/* Settings slide-over */}
      <CampaignSettings
        open={showSettings}
        onClose={() => setShowSettings(false)}
        session={session}
        onSave={handleSaveSettings}
        saving={savingSettings}
        error={error}
        responseCount={responses.length}
      />
    </div>
  );
};

export default InterviewDetailView;
